import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, Dialog, Typography } from "@mui/material";
import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import {
  GET_ALL_CATEGORIES,
  PATCH_CATEGORY,
  STORE_SETUP,
  STORE_SETUP_TAX,
  STORE_SETUP_TAXES,
} from "../../../../configs/apiConfig";
import { Colors, DEFAULT_BRAND, primaryAlpha } from "../../../../configs/colors";
import { useAppSelector } from "../../../../hooks";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { BaseResponseSingle } from "../../../../typings";
import { CategoryResponse } from "../../../../typings/interfaces/responses/category-response";
import {
  IStoreHour,
  IStoreSetup,
  IStoreTax,
  StoreDateFormat,
  StoreTimeFormat,
  WeekDay,
} from "../../../../typings/interfaces/responses/store-setup-response";
import { setStoreSetup } from "../../../../util/store-header";
import { US_STATES, STORE_TIME_ZONES, THEME_COLORS } from "./storeSetupOptions";

const BORDER = "#E5E7EB";
const TEXT = "#4B5563";
const MUTED = "#6B7280";
const ERROR = "#DC2626";

const WEEK: WeekDay[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const dayLabel = (day: WeekDay) => day.charAt(0) + day.slice(1).toLowerCase();

const DATE_FORMATS: StoreDateFormat[] = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"];
const TIME_FORMATS: { value: StoreTimeFormat; label: string }[] = [
  { value: "12H", label: "12 Hour (AM/PM)" },
  { value: "24H", label: "24 Hour" },
];

// Logos are scaled to fit this box before they're saved
const LOGO_MAX_SIDE = 400;
const LOGO_MAX_LENGTH = 700_000;

// ── Form ─────────────────────────────────────────────────────────────────────

interface IStoreForm {
  storeName: string;
  address: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  phone2: string;
  email: string;
  website: string;
  slogan: string;
  primaryColor: string;
  secondaryColor: string;
  logo: string;
  deliveryEnabled: boolean;
  timezone: string;
  dateFormat: StoreDateFormat;
  timeFormat: StoreTimeFormat;
  storeHours: IStoreHour[];
}

type TextField = Exclude<
  keyof IStoreForm,
  "deliveryEnabled" | "storeHours" | "dateFormat" | "timeFormat" | "primaryColor" | "secondaryColor" | "logo"
>;

const toForm = (s: IStoreSetup): IStoreForm => ({
  storeName: s.storeName ?? "",
  address: s.address ?? "",
  unit: s.unit ?? "",
  city: s.city ?? "",
  state: s.state ?? "",
  zip: s.zip ?? "",
  phone: s.phone ?? "",
  phone2: s.phone2 ?? "",
  email: s.email ?? "",
  website: s.website ?? "",
  slogan: s.slogan ?? "",
  primaryColor: s.primaryColor ?? DEFAULT_BRAND.primary,
  secondaryColor: s.secondaryColor ?? DEFAULT_BRAND.secondary,
  logo: s.logo ?? "",
  deliveryEnabled: s.deliveryEnabled !== false,
  timezone: s.timezone || "America/New_York",
  dateFormat: s.dateFormat ?? "MM/DD/YYYY",
  timeFormat: s.timeFormat ?? "12H",
  storeHours: WEEK.map(
    (day) =>
      s.storeHours?.find((h) => h.day === day) ?? { day, enabled: false, openTime: null, closeTime: null }
  ),
});

// Formatting while typing, and the rules the BE checks on save
const FIELD_RULES: Partial<Record<TextField, { format?(v: string): string; valid(v: string): boolean; hint: string }>> = {
  phone: {
    format: (v) => {
      const d = v.replace(/\D/g, "").slice(0, 10);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
      return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    },
    valid: (v) => v === "" || /^\d{3}-\d{3}-\d{4}$/.test(v),
    hint: "Format: 703-555-0100",
  },
  zip: {
    format: (v) => v.replace(/\D/g, "").slice(0, 5),
    valid: (v) => v === "" || /^\d{5}$/.test(v),
    hint: "5 digits",
  },
  email: {
    valid: (v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v),
    hint: "Must include @ and a domain (e.g. .com)",
  },
  website: {
    valid: (v) => v === "" || /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(v),
    hint: "e.g. www.prestovalet.com",
  },
};
FIELD_RULES.phone2 = FIELD_RULES.phone;

const fieldError = (form: IStoreForm, key: TextField): string | null => {
  if (key === "storeName" && !form.storeName.trim()) return "Store name is required";
  const rule = FIELD_RULES[key];
  return rule && !rule.valid(form[key].trim()) ? rule.hint : null;
};

const hourError = (h: IStoreHour): string | null => {
  if (!h.enabled) return null;
  if (!h.openTime || !h.closeTime) return "Set open and close times";
  return h.openTime >= h.closeTime ? "Must open before it closes" : null;
};

const TEXT_FIELDS: TextField[] = ["storeName", "zip", "phone", "phone2", "email", "website"];

// ── Controls ─────────────────────────────────────────────────────────────────

const Toggle: FC<{ checked: boolean; onChange(next: boolean): void; label: string; disabled?: boolean }> = ({
  checked,
  onChange,
  label,
  disabled,
}) => (
  <Box
    component="button"
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    sx={{
      position: "relative",
      width: "3rem",
      height: "1.6rem",
      flexShrink: 0,
      border: "none",
      borderRadius: "99px",
      p: 0,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      backgroundColor: checked ? Colors.PRIMARY : "#D1D5DB",
      transition: "background-color 0.2s",
      "&:focus-visible": { outline: `2px solid ${Colors.PRIMARY}`, outlineOffset: "2px" },
      "&::after": {
        content: '""',
        position: "absolute",
        top: "0.25rem",
        left: checked ? "1.65rem" : "0.25rem",
        width: "1.1rem",
        height: "1.1rem",
        borderRadius: "50%",
        backgroundColor: "#ffffff",
        transition: "left 0.2s",
      },
    }}
  />
);

const inputSx = (invalid?: boolean, disabled?: boolean) => ({
  height: "2.6rem",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box" as const,
  borderRadius: "0.6rem",
  border: `1px solid ${invalid ? ERROR : BORDER}`,
  backgroundColor: disabled ? "#F3F4F6" : "#ffffff",
  color: disabled ? "#9CA3AF" : TEXT,
  fontFamily: "inherit",
  fontSize: "var(--fs-2xs)",
  fontWeight: 500,
  px: "0.75rem",
  outline: "none",
  cursor: disabled ? "not-allowed" : undefined,
  "&:focus": { borderColor: invalid ? ERROR : Colors.PRIMARY, boxShadow: `0 0 0 3px ${primaryAlpha(0.10)}` },
});

const FieldRow: FC<{ label: string; error?: string | null; children: ReactNode }> = ({ label, error, children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "9rem 1fr", alignItems: "center", columnGap: "0.5rem" }}>
    <Typography component="span" sx={{ fontSize: "var(--fs-2xs)", fontWeight: 500, color: TEXT }}>
      {label}
    </Typography>
    {children}
    {error && (
      <Typography sx={{ gridColumn: 2, fontSize: "var(--fs-2xs)", color: ERROR, mt: "0.2rem" }}>{error}</Typography>
    )}
  </Box>
);

const Card: FC<{ title: string; description?: string; action?: ReactNode; testId?: string; children: ReactNode }> = ({
  title,
  description,
  action,
  testId,
  children,
}) => (
  <Box
    component="section"
    aria-label={title}
    data-testid={testId}
    sx={{
      borderRadius: "1.35rem",
      border: `1px solid ${BORDER}`,
      backgroundColor: "#F4F4F4",
      p: "1rem",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      minWidth: 0,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", mb: "0.85rem" }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: "var(--fs-md)", fontWeight: 700, color: TEXT }}>
          {title}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.2rem" }}>{description}</Typography>
        )}
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const AddButton: FC<{ label: string; onClick(): void }> = ({ label, onClick }) => (
  <Box
    component="button"
    type="button"
    aria-label={label}
    onClick={onClick}
    sx={{
      width: "2rem",
      height: "2rem",
      flexShrink: 0,
      borderRadius: "0.55rem",
      border: "none",
      backgroundColor: Colors.PRIMARY,
      color: "#ffffff",
      fontSize: "var(--fs-lg)",
      fontWeight: 700,
      lineHeight: 1,
      cursor: "pointer",
      "&:hover": { backgroundColor: Colors.PRIMARY_DARK },
    }}
  >
    +
  </Box>
);

const Button: FC<{ primary?: boolean; disabled?: boolean; onClick(): void; children: ReactNode; danger?: boolean }> = ({
  primary,
  disabled,
  onClick,
  children,
  danger,
}) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onClick}
    sx={{
      height: "3.2rem",
      minWidth: primary ? "11rem" : undefined,
      px: "1.4rem",
      borderRadius: "0.85rem",
      border: primary ? "none" : `1px solid ${danger ? "#FECACA" : BORDER}`,
      backgroundColor: primary ? (disabled ? "#D1D5DB" : Colors.PRIMARY) : "#ffffff",
      color: primary ? "#ffffff" : danger ? ERROR : TEXT,
      fontFamily: "inherit",
      fontSize: "var(--fs-xs)",
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: !primary && disabled ? 0.5 : 1,
      "&:hover": primary && !disabled ? { backgroundColor: Colors.PRIMARY_DARK } : undefined,
    }}
  >
    {children}
  </Box>
);

const ConfigModal: FC<{ title: string; open: boolean; onClose(): void; children: ReactNode; footer: ReactNode }> = ({
  title,
  open,
  onClose,
  children,
  footer,
}) => (
  <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "1.5rem", width: "34rem", maxWidth: "95vw" } }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: "#F8F8F8",
        p: "1rem 1.5rem",
      }}
    >
      <Box>
        <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, letterSpacing: "0.14em", color: "#9CA3AF", textTransform: "uppercase" }}>
          Configuration
        </Typography>
        <Typography component="h2" sx={{ fontSize: "var(--fs-lg)", fontWeight: 700, color: TEXT, mt: "0.2rem" }}>
          {title}
        </Typography>
      </Box>
      <Box
        component="button"
        type="button"
        aria-label="Close"
        onClick={onClose}
        sx={{
          width: "2.6rem",
          height: "2.6rem",
          borderRadius: "0.7rem",
          border: `1px solid ${BORDER}`,
          backgroundColor: "#ffffff",
          fontSize: "var(--fs-lg)",
          color: MUTED,
          cursor: "pointer",
        }}
      >
        ×
      </Box>
    </Box>
    <Box sx={{ p: "1.5rem", display: "flex", flexDirection: "column", gap: "1.2rem" }}>
      <Box
        sx={{
          borderRadius: "1rem",
          border: `1px solid ${BORDER}`,
          backgroundColor: "#FAFAFA",
          p: "1.2rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.85rem",
        }}
      >
        {children}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem", borderTop: `1px solid ${BORDER}`, pt: "1.2rem" }}>
        {footer}
      </Box>
    </Box>
  </Dialog>
);

const ModalRow: FC<{ label: string; children: ReactNode; error?: string | null }> = ({ label, children, error }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "11rem 1fr", alignItems: "center", columnGap: "1rem" }}>
    <Typography component="label" sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: TEXT }}>
      {label}
    </Typography>
    {children}
    {error && <Typography sx={{ gridColumn: 2, fontSize: "var(--fs-2xs)", color: ERROR, mt: "0.2rem" }}>{error}</Typography>}
  </Box>
);

const PercentInput: FC<{ value: string; onChange(v: string): void; label: string }> = ({ value, onChange, label }) => (
  <Box sx={{ position: "relative" }}>
    <Box
      component="input"
      type="number"
      inputMode="decimal"
      min={0}
      max={100}
      step={0.01}
      placeholder="0.00"
      aria-label={label}
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{ ...inputSx(), pr: "2rem", MozAppearance: "textfield" }}
    />
    <Typography
      component="span"
      sx={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", fontSize: "var(--fs-xs)", fontWeight: 700, color: MUTED, pointerEvents: "none" }}
    >
      %
    </Typography>
  </Box>
);

// ── Theme ────────────────────────────────────────────────────────────────────

const ColorTile: FC<{ label: string; value: string; onChange(v: string): void }> = ({ label, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <Box ref={ref} sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box
        component="button"
        type="button"
        aria-label={`${label}: ${value}`}
        onClick={() => setOpen((v) => !v)}
        sx={{
          width: "6rem",
          height: "4.6rem",
          borderRadius: "0.9rem",
          border: `1px solid ${BORDER}`,
          backgroundColor: value,
          cursor: "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        }}
      />
      <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: TEXT, mt: "0.4rem", textAlign: "center" }}>
        {label}
      </Typography>
      {open && (
        <Box
          sx={{
            position: "absolute",
            bottom: "6.4rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            width: "15rem",
            borderRadius: "1rem",
            border: `1px solid ${BORDER}`,
            backgroundColor: "#ffffff",
            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
            p: "0.85rem",
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.6rem" }}>
            {THEME_COLORS.map((c) => (
              <Box
                key={c}
                component="button"
                type="button"
                aria-label={c}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
                sx={{
                  width: "2.3rem",
                  height: "2.3rem",
                  borderRadius: "0.6rem",
                  border: value.toUpperCase() === c ? `2px solid ${Colors.PRIMARY}` : `1px solid ${BORDER}`,
                  backgroundColor: c,
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
          <Box
            component="label"
            sx={{
              mt: "0.7rem",
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              borderRadius: "0.7rem",
              border: `1px solid ${BORDER}`,
              backgroundColor: "#F9FAFB",
              p: "0.4rem 0.6rem",
              cursor: "pointer",
            }}
          >
            <Box
              component="input"
              type="color"
              aria-label={`Custom ${label}`}
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value.toUpperCase())}
              sx={{ width: "1.6rem", height: "1.6rem", p: 0, border: "none", background: "none", cursor: "pointer" }}
            />
            <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: TEXT }}>{value} · Custom</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Scales raster logos down to LOGO_MAX_SIDE; SVGs are kept as they are
const readLogo = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the image"));
    reader.onload = () => {
      const dataUrl = String(reader.result);
      if (file.type === "image/svg+xml") return resolve(dataUrl);
      const img = new Image();
      img.onerror = () => reject(new Error("Could not read the image"));
      img.onload = () => {
        const scale = Math.min(1, LOGO_MAX_SIDE / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });

const LogoUpload: FC<{ value: string; onChange(v: string): void; onError(message: string): void }> = ({
  value,
  onChange,
  onError,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        data-testid="logo-input"
        style={{ display: "none" }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          try {
            const logo = await readLogo(file);
            if (logo.length > LOGO_MAX_LENGTH) throw new Error("Logo must be 500 KB or smaller");
            onChange(logo);
          } catch (err: any) {
            onError(err?.message || "Could not read the image");
          }
        }}
      />
      <Box
        component="button"
        type="button"
        aria-label={value ? "Change logo" : "Upload logo"}
        onClick={() => fileRef.current?.click()}
        sx={{
          width: "6rem",
          height: "4.6rem",
          borderRadius: "0.9rem",
          border: "1.5px dashed #D1D5DB",
          backgroundColor: "#ffffff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          p: value ? "0.3rem" : 0,
          "&:hover": { borderColor: Colors.PRIMARY },
        }}
      >
        {value ? (
          <Box component="img" src={value} alt="Store logo" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: MUTED }}>Upload Logo</Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.4rem", mt: "0.4rem" }}>
        <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: TEXT }}>Logo</Typography>
        {value && (
          <Box
            component="button"
            type="button"
            onClick={() => onChange("")}
            sx={{ border: "none", background: "none", p: 0, fontFamily: "inherit", fontSize: "var(--fs-2xs)", color: ERROR, cursor: "pointer" }}
          >
            Remove
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Time zone ────────────────────────────────────────────────────────────────

const TimezoneClock: FC<{ value: string; onChange(v: string): void }> = ({ value, onChange }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const zones = STORE_TIME_ZONES.some((z) => z.iana === value)
    ? STORE_TIME_ZONES
    : [{ label: value, iana: value }, ...STORE_TIME_ZONES];
  const label = zones.find((z) => z.iana === value)?.label ?? value;
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const safe = (fn: () => string) => {
    try {
      return fn();
    } catch {
      return "";
    }
  };
  const time = safe(() => now.toLocaleTimeString("en-US", { timeZone: value, hour: "2-digit", minute: "2-digit", second: "2-digit" }));
  const date = safe(() =>
    now.toLocaleDateString("en-US", { timeZone: value, weekday: "long", month: "long", day: "numeric", year: "numeric" })
  );
  const abbr = safe(() => now.toLocaleTimeString("en-US", { timeZone: value, timeZoneName: "short" }).split(" ").slice(-1)[0]);

  return (
    <Box sx={{ mt: "0.85rem", borderRadius: "1rem", border: `1px solid ${BORDER}`, backgroundColor: "#F4F4F4", p: "0.9rem 1rem" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "0.75rem" }}>
        <Box>
          <Typography component="h3" sx={{ fontSize: "var(--fs-base)", fontWeight: 700, color: TEXT }}>
            Time Zone
          </Typography>
          <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.2rem" }}>
            Applies to store hours, ready dates and auto-close
          </Typography>
        </Box>
        {value === detected && (
          <Typography
            component="span"
            sx={{
              fontSize: "var(--fs-2xs)",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              backgroundColor: "#E8F5EE",
              color: Colors.PRIMARY,
              borderRadius: "99px",
              p: "0.2rem 0.7rem",
              border: "1px solid #BFEAD3",
            }}
          >
            This device
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          borderRadius: "0.8rem",
          backgroundColor: "#ffffff",
          border: `1px solid ${BORDER}`,
          p: "0.75rem 1rem",
          mb: "0.75rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <Box>
          <Typography data-testid="store-clock" sx={{ fontSize: "var(--fs-2xl)", fontWeight: 800, color: "#111827", fontVariantNumeric: "tabular-nums" }}>
            {time}
          </Typography>
          <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.2rem" }}>{date}</Typography>
        </Box>
        <Box sx={{ textAlign: "right", minWidth: 0 }}>
          <Typography sx={{ fontSize: "var(--fs-md)", fontWeight: 700, color: Colors.PRIMARY }}>{abbr}</Typography>
          <Typography sx={{ fontSize: "var(--fs-2xs)", color: "#9CA3AF", mt: "0.2rem" }}>{label}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
        <Box
          component="select"
          aria-label="Time zone"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
          sx={{ ...inputSx(), flex: 1, cursor: "pointer" }}
        >
          {zones.map((z) => (
            <option key={z.iana} value={z.iana}>
              {z.label}
            </option>
          ))}
        </Box>
        <Box
          component="button"
          type="button"
          disabled={!STORE_TIME_ZONES.some((z) => z.iana === detected)}
          onClick={() => onChange(detected)}
          sx={{
            height: "2.6rem",
            px: "0.9rem",
            borderRadius: "0.6rem",
            border: `1px solid ${BORDER}`,
            backgroundColor: "#ffffff",
            fontFamily: "inherit",
            fontSize: "var(--fs-2xs)",
            fontWeight: 600,
            color: TEXT,
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
          }}
        >
          Use This Device's Zone
        </Box>
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

interface ITaxDraft {
  id?: number;
  name: string;
  rate: string;
  includeInPrice: boolean;
}

interface IServiceDraft {
  name: string;
  code: string;
  maxItems: string;
}

const isService = (c: CategoryResponse, all: CategoryResponse[]) =>
  !c.deleted && !all.some((o) => !o.deleted && o.alterationCategoryId === c.id);

export const StoreSetupPage: FC = () => {
  const { showSnackbar } = useSnackbarContext();
  const { adminDashboardSelectedStore, adminDashboardSelectedMerchant } = useAppSelector((state) => state.master);
  const storeId = adminDashboardSelectedStore?.storeId;
  const merchantId = adminDashboardSelectedMerchant?.merchantId;

  const [setup, setSetup] = useState<IStoreSetup | null>(null);
  const [form, setForm] = useState<IStoreForm | null>(null);
  const [touched, setTouched] = useState<Partial<Record<TextField, boolean>>>({});
  const [taxes, setTaxes] = useState<IStoreTax[]>([]);
  const [services, setServices] = useState<CategoryResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [taxDraft, setTaxDraft] = useState<ITaxDraft | null>(null);
  const [serviceDraft, setServiceDraft] = useState<IServiceDraft | null>(null);

  const load = useCallback(async () => {
    if (!storeId || !merchantId) return;
    setLoading(true);
    try {
      const [setupRes, categoryRes] = await Promise.all([
        axios.get<BaseResponseSingle<IStoreSetup>>(STORE_SETUP(storeId, merchantId)),
        axios.get<BaseResponseSingle<CategoryResponse[]>>(GET_ALL_CATEGORIES),
      ]);
      if (setupRes.data?.status !== "SUCCESS" || !setupRes.data.body) {
        throw new Error(setupRes.data?.message || "Could not load the store setup");
      }
      setSetup(setupRes.data.body);
      setForm(toForm(setupRes.data.body));
      setTaxes(setupRes.data.body.taxes ?? []);
      setTouched({});
      setServices(categoryRes.data?.body ?? []);
    } catch (err: any) {
      setSetup(null);
      setForm(null);
      showSnackbar(err?.message || "Could not load the store setup", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () => !!setup && !!form && JSON.stringify(form) !== JSON.stringify(toForm(setup)),
    [setup, form]
  );
  const invalid = useMemo(
    () => !!form && (TEXT_FIELDS.some((k) => fieldError(form, k)) || form.storeHours.some((h) => hourError(h))),
    [form]
  );

  const update = (patch: Partial<IStoreForm>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const updateHour = (day: WeekDay, patch: Partial<IStoreHour>) =>
    setForm((f) =>
      f
        ? {
            ...f,
            storeHours: f.storeHours.map((h) => {
              if (h.day !== day) return h;
              const next = { ...h, ...patch };
              // Turning a day on starts it with the usual hours
              if (patch.enabled && !h.openTime) next.openTime = "07:00";
              if (patch.enabled && !h.closeTime) next.closeTime = "18:00";
              return next;
            }),
          }
        : f
    );

  const shownError = (key: TextField) => (form && touched[key] ? fieldError(form, key) : null);

  const save = async () => {
    if (!form || !storeId || !merchantId) return;
    if (invalid) {
      setTouched(Object.fromEntries(TEXT_FIELDS.map((k) => [k, true])));
      showSnackbar("Fix the highlighted fields before saving", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put<BaseResponseSingle<IStoreSetup>>(STORE_SETUP(storeId, merchantId), {
        ...form,
        logo: form.logo || null,
      });
      if (res.data?.status !== "SUCCESS" || !res.data.body) {
        throw new Error(res.data?.message || "Could not save the store setup");
      }
      setSetup(res.data.body);
      setForm(toForm(res.data.body));
      setTouched({});
      setStoreSetup(res.data.body);
      showSnackbar("Store setup saved", "success");
    } catch (err: any) {
      showSnackbar(err?.message || "Could not save the store setup", "error");
    } finally {
      setSaving(false);
    }
  };

  // Services and taxes save as soon as they change

  const patchService = async (service: CategoryResponse, changes: Partial<CategoryResponse>) => {
    setBusy(true);
    try {
      const res = await axios.patch<BaseResponseSingle<CategoryResponse>>(PATCH_CATEGORY(service.id), changes);
      if (res.data?.status !== "SUCCESS" || !res.data.body) throw new Error(res.data?.message || "Could not update the service");
      const saved = res.data.body;
      setServices((all) => all.map((c) => (c.id === saved.id ? { ...c, ...saved } : c)));
      return saved;
    } catch (err: any) {
      showSnackbar(err?.message || "Could not update the service", "error");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const addService = async () => {
    if (!serviceDraft) return;
    const name = serviceDraft.name.trim();
    const maxItems = serviceDraft.maxItems.trim() ? Number(serviceDraft.maxItems) : null;
    if (!name) return showSnackbar("Enter a service name", "error");
    if (services.some((c) => !c.deleted && c.categoryName.trim().toLowerCase() === name.toLowerCase())) {
      return showSnackbar(`${name} already exists`, "error");
    }
    if (maxItems !== null && (!Number.isInteger(maxItems) || maxItems < 1)) {
      return showSnackbar("Maximum items must be a whole number of at least 1", "error");
    }
    setBusy(true);
    try {
      const code = serviceDraft.code || name.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
      const orderIndex = Math.max(0, ...services.map((c) => c.orderIndex ?? 0)) + 1;
      const created = await axios.post<BaseResponseSingle<CategoryResponse>>(GET_ALL_CATEGORIES, {
        categoryName: name,
        categoryCode: code || null,
        orderIndex,
      });
      if (created.data?.status !== "SUCCESS" || !created.data.body) {
        throw new Error(created.data?.message || "Could not add the service");
      }
      const res = await axios.patch<BaseResponseSingle<CategoryResponse>>(PATCH_CATEGORY(created.data.body.id), {
        showInMenu: true,
        maxItemsPerBill: maxItems,
      });
      setServices((all) => [...all, res.data?.body ?? created.data.body]);
      setServiceDraft(null);
      showSnackbar(`${name} added`, "success");
    } catch (err: any) {
      showSnackbar(err?.message || "Could not add the service", "error");
    } finally {
      setBusy(false);
    }
  };

  const taxRateError = (rate: string) => {
    if (rate.trim() === "") return "Enter the tax amount";
    const n = Number(rate);
    if (Number.isNaN(n) || n < 0 || n > 100) return "Must be between 0 and 100";
    return /^\d+(\.\d{1,3})?$/.test(rate.trim()) ? null : "At most 3 decimal places";
  };

  const saveTax = async () => {
    if (!taxDraft || !storeId || !merchantId) return;
    if (!taxDraft.name.trim()) return showSnackbar("Enter a tax name", "error");
    const rateError = taxRateError(taxDraft.rate);
    if (rateError) return showSnackbar(rateError, "error");
    setBusy(true);
    try {
      const body = { name: taxDraft.name.trim(), rate: Number(taxDraft.rate), includeInPrice: taxDraft.includeInPrice };
      const res = taxDraft.id
        ? await axios.patch<BaseResponseSingle<IStoreTax>>(STORE_SETUP_TAX(taxDraft.id), body)
        : await axios.post<BaseResponseSingle<IStoreTax>>(STORE_SETUP_TAXES(storeId, merchantId), { ...body, enabled: true });
      if (res.data?.status !== "SUCCESS" || !res.data.body) throw new Error(res.data?.message || "Could not save the tax");
      const saved = res.data.body;
      setTaxes((all) => (taxDraft.id ? all.map((t) => (t.id === saved.id ? saved : t)) : [...all, saved]));
      setTaxDraft(null);
      showSnackbar(taxDraft.id ? "Tax updated" : "Tax added", "success");
    } catch (err: any) {
      showSnackbar(err?.message || "Could not save the tax", "error");
    } finally {
      setBusy(false);
    }
  };

  const toggleTax = async (tax: IStoreTax, enabled: boolean) => {
    setBusy(true);
    try {
      const res = await axios.patch<BaseResponseSingle<IStoreTax>>(STORE_SETUP_TAX(tax.id), { enabled });
      if (res.data?.status !== "SUCCESS" || !res.data.body) throw new Error(res.data?.message || "Could not update the tax");
      const saved = res.data.body;
      setTaxes((all) => all.map((t) => (t.id === saved.id ? saved : t)));
    } catch (err: any) {
      showSnackbar(err?.message || "Could not update the tax", "error");
    } finally {
      setBusy(false);
    }
  };

  const deleteTax = async () => {
    if (!taxDraft?.id) return;
    setBusy(true);
    try {
      const res = await axios.delete<BaseResponseSingle<null>>(STORE_SETUP_TAX(taxDraft.id));
      if (res.data?.status !== "SUCCESS") throw new Error(res.data?.message || "Could not delete the tax");
      setTaxes((all) => all.filter((t) => t.id !== taxDraft.id));
      setTaxDraft(null);
      showSnackbar("Tax deleted", "success");
    } catch (err: any) {
      showSnackbar(err?.message || "Could not delete the tax", "error");
    } finally {
      setBusy(false);
    }
  };

  const textInput = (key: TextField, label: string, placeholder: string) => (
    <FieldRow key={key} label={label} error={shownError(key)}>
      <Box
        component="input"
        aria-label={label}
        value={form?.[key] ?? ""}
        placeholder={placeholder}
        aria-invalid={!!shownError(key)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const rule = FIELD_RULES[key];
          update({ [key]: rule?.format ? rule.format(e.target.value) : e.target.value } as Partial<IStoreForm>);
        }}
        onBlur={() => setTouched((t) => ({ ...t, [key]: true }))}
        sx={inputSx(!!shownError(key))}
      />
    </FieldRow>
  );

  const shownServices = services
    .filter((c) => isService(c, services))
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  return (
    <AdminLayout>
      {!storeId || !merchantId ? (
        <Typography sx={{ fontSize: "var(--fs-base)", color: MUTED }}>Select a store in the header to set it up.</Typography>
      ) : loading || !form ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: "3rem" }}>{loading ? <CircularProgress /> : null}</Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Save bar */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "var(--fs-xs)", color: MUTED }}>
              Store details for <strong>{adminDashboardSelectedStore?.storeName}</strong>
              {dirty ? " · Unsaved changes" : setup?.configured ? "" : " · Not saved yet — receipts use the default details"}
            </Typography>
            <Box sx={{ display: "flex", gap: "0.7rem" }}>
              <Button
                disabled={!dirty || saving}
                onClick={() => {
                  if (setup) setForm(toForm(setup));
                  setTouched({});
                }}
              >
                Discard
              </Button>
              <Button primary disabled={(!dirty && !!setup?.configured) || saving} onClick={save}>
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "4fr 3fr 5fr" },
              gap: "1rem",
              alignItems: "start",
            }}
          >
            {/* Store Info */}
            <Card title="Store Info" description="Printed on receipts, tickets and reports." testId="store-info">
              <Box sx={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                {textInput("storeName", "Store Name", "Presto Valet")}
                {textInput("address", "Address", "1623 N Quaker Ln")}
                {textInput("unit", "Unit", "Suite 102")}
                {textInput("city", "City", "Alexandria")}
                <FieldRow label="State">
                  <Box
                    component="select"
                    aria-label="State"
                    value={form.state}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => update({ state: e.target.value })}
                    sx={{ ...inputSx(), cursor: "pointer" }}
                  >
                    <option value="">Select state</option>
                    {!US_STATES.some((s) => s.code === form.state) && form.state && (
                      <option value={form.state}>{form.state}</option>
                    )}
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </Box>
                </FieldRow>
                {textInput("zip", "Zip Code", "22302")}
                {textInput("phone", "Primary Phone", "703-555-0100")}
                {textInput("phone2", "Secondary Phone", "703-555-0101")}
                {textInput("email", "Email", "info@prestovalet.com")}
                {textInput("website", "Website", "www.prestovalet.com")}
                {textInput("slogan", "Business Slogan", "Luxury Garment Care Since 1951")}
              </Box>
              <Box sx={{ mt: "1rem" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "0.6rem", mb: "0.85rem" }}>
                  <Typography sx={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: TEXT }}>Theme</Typography>
                  <Box sx={{ flex: 1, height: "1px", backgroundColor: BORDER }} />
                </Box>
                <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "-0.4rem", mb: "0.85rem" }}>
                  Colours and logo apply to the POS and this admin panel after saving.
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.85rem" }}>
                  <ColorTile label="Primary Color" value={form.primaryColor} onChange={(primaryColor) => update({ primaryColor })} />
                  <ColorTile
                    label="Secondary Color"
                    value={form.secondaryColor}
                    onChange={(secondaryColor) => update({ secondaryColor })}
                  />
                  <LogoUpload value={form.logo} onChange={(logo) => update({ logo })} onError={(m) => showSnackbar(m, "error")} />
                </Box>
              </Box>
            </Card>

            {/* Middle column */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", minWidth: 0 }}>
              <Card
                title="Services"
                description="Turned-off services are hidden from the POS."
                testId="services"
                action={<AddButton label="Add service" onClick={() => setServiceDraft({ name: "", code: "", maxItems: "" })} />}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                  {shownServices.length === 0 && (
                    <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED }}>No services yet.</Typography>
                  )}
                  {shownServices.map((s) => (
                    <Box key={s.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
                        {s.categoryCode && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "var(--fs-2xs)",
                              fontWeight: 800,
                              letterSpacing: "0.08em",
                              color: Colors.SECONDARY,
                              backgroundColor: "#EAF5FC",
                              border: "1px solid #BFDEF2",
                              borderRadius: "0.35rem",
                              p: "0.05rem 0.4rem",
                              fontFamily: "var(--font-mono)",
                              minWidth: "2.2rem",
                              textAlign: "center",
                            }}
                          >
                            {s.categoryCode}
                          </Typography>
                        )}
                        <Typography
                          sx={{ fontSize: "var(--fs-2xs)", fontWeight: 500, color: s.showInMenu ? TEXT : "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {s.categoryName}
                        </Typography>
                      </Box>
                      <Toggle
                        label={`${s.categoryName} enabled`}
                        checked={s.showInMenu}
                        disabled={busy}
                        onChange={(showInMenu) => patchService(s, { showInMenu })}
                      />
                    </Box>
                  ))}
                </Box>
              </Card>

              <Card
                title="Delivery"
                testId="delivery"
                action={
                  <Toggle label="Pickup & delivery" checked={form.deliveryEnabled} onChange={(deliveryEnabled) => update({ deliveryEnabled })} />
                }
              >
                <Box sx={{ borderRadius: "0.7rem", border: `1px solid ${BORDER}`, backgroundColor: "#ffffff", p: "0.7rem 0.85rem" }}>
                  <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: TEXT }}>Pickup & Delivery Service</Typography>
                  <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.25rem" }}>
                    Enable or disable customer pickup and delivery scheduling.
                  </Typography>
                </Box>
              </Card>

              <Card
                title="Taxes"
                description="Saved for this store. Bills don't apply these rates yet."
                testId="taxes"
                action={<AddButton label="Add tax" onClick={() => setTaxDraft({ name: "", rate: "", includeInPrice: true })} />}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                  {taxes.length === 0 && <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED }}>No taxes yet.</Typography>}
                  {taxes.map((t) => (
                    <Box
                      key={t.id}
                      data-testid="tax-row"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.6rem",
                        borderRadius: "0.7rem",
                        border: `1px solid ${BORDER}`,
                        backgroundColor: "#ffffff",
                        p: "0.55rem 0.85rem",
                        "&:hover .tax-edit, & .tax-edit:focus-visible": { opacity: 1 },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: t.enabled ? TEXT : "#9CA3AF" }}>{t.name}</Typography>
                        <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.1rem" }}>
                          {Number(t.rate).toFixed(2)}%{t.includeInPrice ? " · included in price" : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <Box
                          component="button"
                          type="button"
                          className="tax-edit"
                          aria-label={`Edit ${t.name}`}
                          onClick={() => setTaxDraft({ id: t.id, name: t.name, rate: String(t.rate), includeInPrice: t.includeInPrice })}
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.15s",
                            height: "2rem",
                            borderRadius: "0.5rem",
                            border: `1px solid ${BORDER}`,
                            backgroundColor: "#ffffff",
                            px: "0.7rem",
                            fontFamily: "inherit",
                            fontSize: "var(--fs-2xs)",
                            fontWeight: 700,
                            color: TEXT,
                            cursor: "pointer",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                          }}
                        >
                          Edit
                        </Box>
                        <Toggle label={`${t.name} enabled`} checked={t.enabled} disabled={busy} onChange={(on) => toggleTax(t, on)} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Box>

            {/* Store Hours */}
            <Card title="Store Hours" description="Open days are also the working days used for ready dates." testId="store-hours">
              <Box sx={{ borderRadius: "1rem", border: `1px solid ${BORDER}`, backgroundColor: "#ffffff", overflow: "hidden" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 4rem 8rem 8rem",
                    borderBottom: `1px solid ${BORDER}`,
                    backgroundColor: "#F8F8F8",
                    p: "0.55rem 0.9rem",
                  }}
                >
                  {["Day", "Open", "From", "To"].map((h, i) => (
                    <Typography
                      key={h}
                      sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, letterSpacing: "0.08em", color: MUTED, textTransform: "uppercase", textAlign: i > 0 ? "center" : "left" }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>
                {form.storeHours.map((h, i) => {
                  const error = hourError(h);
                  return (
                    <Box
                      key={h.day}
                      data-testid={`hours-${h.day}`}
                      sx={{
                        p: "0.45rem 0.9rem",
                        borderBottom: i < 6 ? `1px solid ${BORDER}` : "none",
                        backgroundColor: i % 2 === 0 ? "#F9F9F9" : "#ffffff",
                      }}
                    >
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 4rem 8rem 8rem", alignItems: "center", columnGap: "0.4rem" }}>
                        <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: TEXT }}>{dayLabel(h.day)}</Typography>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Toggle label={`${dayLabel(h.day)} open`} checked={h.enabled} onChange={(enabled) => updateHour(h.day, { enabled })} />
                        </Box>
                        {(["openTime", "closeTime"] as const).map((field) => (
                          <Box
                            key={field}
                            component="input"
                            type="time"
                            aria-label={`${dayLabel(h.day)} ${field === "openTime" ? "opens" : "closes"}`}
                            value={h[field] ?? ""}
                            disabled={!h.enabled}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateHour(h.day, { [field]: e.target.value || null })}
                            sx={{ ...inputSx(!!error, !h.enabled), height: "2.3rem", px: "0.4rem" }}
                          />
                        ))}
                      </Box>
                      {error && (
                        <Typography sx={{ fontSize: "var(--fs-2xs)", color: ERROR, textAlign: "right", mt: "0.2rem" }}>{error}</Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <TimezoneClock value={form.timezone} onChange={(timezone) => update({ timezone })} />

              <Box sx={{ mt: "0.7rem", borderRadius: "1rem", border: `1px solid ${BORDER}`, backgroundColor: "#F4F4F4", p: "0.8rem 1rem" }}>
                <Typography component="h3" sx={{ fontSize: "var(--fs-base)", fontWeight: 700, color: TEXT }}>
                  Date & Time Format
                </Typography>
                <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.2rem", mb: "0.65rem" }}>
                  Dates and times on screens and printed tickets
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem" }}>
                  <Box>
                    <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, letterSpacing: "0.08em", color: MUTED, textTransform: "uppercase", mb: "0.4rem" }}>
                      Date Format
                    </Typography>
                    <Box
                      component="select"
                      aria-label="Date format"
                      value={form.dateFormat}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => update({ dateFormat: e.target.value as StoreDateFormat })}
                      sx={{ ...inputSx(), cursor: "pointer" }}
                    >
                      {DATE_FORMATS.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, letterSpacing: "0.08em", color: MUTED, textTransform: "uppercase", mb: "0.4rem" }}>
                      Time Format
                    </Typography>
                    <Box
                      component="select"
                      aria-label="Time format"
                      value={form.timeFormat}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => update({ timeFormat: e.target.value as StoreTimeFormat })}
                      sx={{ ...inputSx(), cursor: "pointer" }}
                    >
                      {TIME_FORMATS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Box>

          {/* Add service */}
          <ConfigModal
            title="Add New Service"
            open={!!serviceDraft}
            onClose={() => setServiceDraft(null)}
            footer={
              <>
                <Button onClick={() => setServiceDraft(null)}>Cancel</Button>
                <Button primary disabled={busy || !serviceDraft?.name.trim()} onClick={addService}>
                  Add Service
                </Button>
              </>
            }
          >
            <ModalRow label="Service Name">
              <Box
                component="input"
                aria-label="Service name"
                placeholder="e.g. Dry Cleaning"
                value={serviceDraft?.name ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceDraft((d) => (d ? { ...d, name: e.target.value } : d))}
                sx={inputSx()}
              />
            </ModalRow>
            <ModalRow label="Service Code">
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
                <Box
                  component="input"
                  aria-label="Service code"
                  placeholder="DC"
                  maxLength={2}
                  value={serviceDraft?.code ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setServiceDraft((d) => (d ? { ...d, code: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) } : d))
                  }
                  sx={{ ...inputSx(), width: "5rem", textAlign: "center", fontWeight: 700, letterSpacing: "0.1em", fontFamily: "var(--font-mono)" }}
                />
                <Typography sx={{ fontSize: "var(--fs-2xs)", color: "#9CA3AF" }}>2 letters · printed on invoices</Typography>
              </Box>
            </ModalRow>
            <ModalRow label="Maximum items per invoice">
              <Box
                component="input"
                type="number"
                min={1}
                step={1}
                aria-label="Maximum items per invoice"
                placeholder="No limit"
                value={serviceDraft?.maxItems ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceDraft((d) => (d ? { ...d, maxItems: e.target.value } : d))}
                sx={inputSx()}
              />
            </ModalRow>
          </ConfigModal>

          {/* Add / edit tax */}
          <ConfigModal
            title={taxDraft?.id ? "Edit Tax" : "Add New Tax"}
            open={!!taxDraft}
            onClose={() => setTaxDraft(null)}
            footer={
              <>
                {taxDraft?.id && (
                  <Box sx={{ mr: "auto" }}>
                    <Button danger disabled={busy} onClick={deleteTax}>
                      Delete
                    </Button>
                  </Box>
                )}
                <Button onClick={() => setTaxDraft(null)}>Cancel</Button>
                <Button primary disabled={busy || !taxDraft?.name.trim() || !!taxRateError(taxDraft?.rate ?? "")} onClick={saveTax}>
                  {taxDraft?.id ? "Save Changes" : "Add Tax"}
                </Button>
              </>
            }
          >
            <ModalRow label="Tax">
              <Box
                component="input"
                aria-label="Tax name"
                placeholder="Tax name"
                value={taxDraft?.name ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaxDraft((d) => (d ? { ...d, name: e.target.value } : d))}
                sx={inputSx()}
              />
            </ModalRow>
            <ModalRow label="Tax Amount" error={taxDraft && taxDraft.rate !== "" ? taxRateError(taxDraft.rate) : null}>
              <PercentInput label="Tax amount" value={taxDraft?.rate ?? ""} onChange={(rate) => setTaxDraft((d) => (d ? { ...d, rate } : d))} />
            </ModalRow>
            <ModalRow label="Include in the Price">
              <Toggle
                label="Include in the price"
                checked={!!taxDraft?.includeInPrice}
                onChange={(includeInPrice) => setTaxDraft((d) => (d ? { ...d, includeInPrice } : d))}
              />
            </ModalRow>
          </ConfigModal>
        </Box>
      )}
    </AdminLayout>
  );
};
